import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table';
import { portalDs, PortalDsBadge } from 'src/components/portal-design-system';
import { Skeleton } from 'src/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import { Client } from '../../types/client.types';
import { ClientAvatar } from './client-avatar';
import { StudioMigratedBadge } from './studio-migrated-badge';
import {
  getClientStatus,
  getUniqueAccountCount,
  getUniqueCampaignCount,
  getWebsiteLabel,
} from '../../utils/client-display.utils';

const TABLE_COLUMN_COUNT = 8;

interface ManageClientsTableProps {
  clients: Client[];
  className?: string;
  loading?: boolean;
  onClientSelect: (client: Client) => void;
}

const ManageClientsTable: React.FC<ManageClientsTableProps> = ({
  className,
  clients,
  loading,
  onClientSelect,
}) => {
  return (
    <div className={cn(portalDs.tableContainer, className)}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-14')} />
            <TableHead className={portalDs.tableHeadCell}>
              Client
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Users
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Accounts
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Campaigns
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Studio
            </TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Status
            </TableHead>
            <TableHead className={cn(portalDs.tableHeadCell, 'w-8')} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 8 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell
                  colSpan={TABLE_COLUMN_COUNT}
                  className={portalDs.tableCell}
                >
                  <Skeleton className="h-10 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : clients.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={TABLE_COLUMN_COUNT}
                className={cn(portalDs.tableCell, 'py-10 text-center', portalDs.tableCellMuted)}
              >
                No clients match this search.
              </TableCell>
            </TableRow>
          ) : (
            clients.map((client) => {
              const status = getClientStatus(client);
              return (
                <TableRow
                  key={client.ext_client_id}
                  className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
                  onClick={() => onClientSelect(client)}
                >
                  <TableCell className={portalDs.tableCell}>
                    <ClientAvatar
                      title={client.title}
                      brand={client.brand}
                    />
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-[#0D0F1A]">
                        {client.title}
                      </div>
                      <div className={portalDs.caption}>
                        {getWebsiteLabel(client.website)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    {client.users?.length ?? 0}
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    {getUniqueAccountCount(client)}
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    {getUniqueCampaignCount(client)}
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    <StudioMigratedBadge client={client} />
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    <PortalDsBadge
                      pill
                      variant={status.variant}
                    >
                      {status.label}
                    </PortalDsBadge>
                  </TableCell>
                  <TableCell className={portalDs.tableCell}>
                    <ChevronRight className="size-4 text-[#9CA3AF]" />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ManageClientsTable;
